import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
  retry,
} from "@reduxjs/toolkit/query/react";
import type { Config } from "../common/interfaces/config";
import type { RootState } from "../store";
import { applyBusinessContextHeaders, EXCLUDED_ENDPOINTS } from "../common/util/businessHeaders";

const buildAuthorization = (state: RootState) => {
  const { token_type = "", access_token } = state.boot.user;
  const tokenType = `${token_type.charAt(0).toUpperCase()}${token_type.slice(1)}`;
  return `${tokenType} ${access_token}`;
};

// Create our baseQuery instance
const rawBaseQuery = fetchBaseQuery({
  prepareHeaders: (headers, { getState, endpoint }) => {
    const rootState = getState() as RootState;
    const authorization = buildAuthorization(rootState);

    headers.set("Authorization", authorization);
    headers.set("Content-Type", "application/json");
    //headers.set("x-mashery-domain", rootState.boot.configuration.requestUrlBase);

    if (!EXCLUDED_ENDPOINTS.has(endpoint)) {
      applyBusinessContextHeaders(headers, rootState);
    }

    return headers;
  },
});

type ApiBasePathKey = keyof Config["apiBasePaths"];

const DOMAIN_KEYS = ["apiCFBaseUrl", "requestUrlBase"] as const;
type DomainKey = (typeof DOMAIN_KEYS)[number];

const getBaseURL = (state: RootState, urlBase?: string, pathKey?: ApiBasePathKey) => {
  const { configuration } = state.boot;

  if (urlBase !== undefined && !DOMAIN_KEYS.includes(urlBase as DomainKey)) {
    return "";
  }

  const domain = DOMAIN_KEYS.includes(urlBase as DomainKey)
    ? (configuration[urlBase as DomainKey] ?? configuration.apiBaseUrl)
    : configuration.apiBaseUrl;
  const path = pathKey ? (configuration.apiBasePaths?.[pathKey] ?? "") : "";
  return domain + path;
};

export const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  { urlBase?: string; pathKey?: ApiBasePathKey }
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const { urlBase, pathKey } = extraOptions ?? {};
  const baseURL = getBaseURL(state, urlBase, pathKey);

  // gracefully handle scenarios where data to generate the URL is missing
  if (!baseURL) {
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        data: "URLBase is not defined",
      },
    };
  }

  const urlEnd = typeof args === "string" ? args : args.url;
  // construct a dynamically generated portion of the url
  const adjustedUrl = `${baseURL}${urlEnd}`;
  const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
  // provide the amended url and other params to the raw base query
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

const dynamicBaseQueryWithRetry = retry(dynamicBaseQuery, {
  maxRetries: 0,
});

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const accAPI = createApi({
  reducerPath: "accAPI",
  baseQuery: dynamicBaseQueryWithRetry,
  endpoints: () => ({}),
});
